import React, { useState, useEffect } from 'react';
import recallService from '../../services/recalls.service';
import { useNavigate } from 'react-router-dom';
import { Collapse, Pagination, Table, Form, InputGroup, Alert, Button } from 'react-bootstrap';
import Loading from "../common/Loading";

function RecallTable() {
    const navigate = useNavigate();
    const [recallList, setRecallList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0
    });

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const getRecallRequest = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Clear recall list when filters change (but not on pagination changes)
                if (searchTerm || statusFilter) {
                    setRecallList([]);
                }

                const res = await recallService.getRecalls(
                    pagination.page,
                    pagination.size,
                    statusFilter,
                    searchTerm,
                    { signal: controller.signal }
                );

                if (isMounted) {
                    setRecallList(res.data.content);
                    setPagination(prev => ({
                        ...prev,
                        totalElements: res.data.totalElements,
                        totalPages: res.data.totalPages
                    }));

                    if (res.data.content.length === 0 && (searchTerm || statusFilter)) {
                        setError('No recalls found matching your filters.');
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError' && isMounted) {
                    console.error("Fetch error:", err);
                    setError('Failed to fetch recalls. Please try again.');
                    setRecallList([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        getRecallRequest();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [pagination.page, pagination.size, statusFilter, searchTerm]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({...prev, page: 0}));
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setPagination(prev => ({...prev, page: 0}));
    };

    const toggleRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id]
        );
    };

    const handleEditClick = (id) => {
        navigate('edit/' + id);
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({...prev, page: page - 1}));
    };

    const handleSizeChange = (e) => {
        setPagination(prev => ({...prev, size: Number(e.target.value), page: 0}));
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'PENDING': { class: 'bg-secondary', text: 'Pending' },
            'IN_PROCESS': { class: 'bg-primary', text: 'In Process' },
            'COMPLETED': { class: 'bg-success', text: 'Completed' },
            'FAILED': { class: 'bg-danger', text: 'Failed' }
        };

        const config = statusConfig[status] || { class: 'bg-secondary', text: status };

        return <span className={`badge ${config.class}`}>{config.text}</span>;
    };

    // Calculate the range of items being displayed
    const startItem = pagination.page * pagination.size + 1;
    const endItem = Math.min((pagination.page + 1) * pagination.size, pagination.totalElements);

    return (
        <div className="container-fluid py-4">
            <div className="card card-padding shadow-sm">
                <div className="break"></div>
                <div className="card-body">
                    {/* Search Filters Section */}
                    <div className="mb-4 p-3 rounded">
                        <form onSubmit={handleSearch}>
                            <div className="row g-3 align-items-end">
                                <div className="col-md-6">
                                    <label>Search by Title</label>
                                    <div className="input-group input-group-sm">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter title"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <label>Filter by Status</label>
                                    <div className="input-group-sm">
                                        <select
                                            className="form-control"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="PENDING">Pending</option>
                                            <option value="IN_PROCESS">In Process</option>
                                            <option value="COMPLETED">Completed</option>
                                            <option value="FAILED">Failed</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="col-md-2 d-flex gap-2">
                                    <button className="btn btn-primary btn-sm" type="submit">
                                        <i className="bi bi-search me-1"></i> Search
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={handleClearFilters}
                                        disabled={!searchTerm && !statusFilter}
                                    >
                                        <i className="bi bi-x"></i> Clear
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    {isLoading ? (
                        <Loading />
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-bordered hover mb-0">
                                    <thead>
                                    <tr>
                                        <th className="col-3"><i className="bi bi-building"></i> Clinic Name</th>
                                        <th className="col-5"><i className="bi bi-file-text"></i> Title</th>
                                        <th className="col-2"><i className="bi bi-calendar"></i> Created At</th>
                                        <th className="col-1"><i className="bi bi-arrow-clockwise"></i> Status</th>
                                        <th className="col-1"><i className="bi bi-gear"></i> Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {recallList.length > 0 ? (
                                        recallList.map(recall => (
                                            <React.Fragment key={recall.id}>
                                                <tr>
                                                    <td>
                                                        <div>{recall.clinicName}</div>
                                                    </td>
                                                    <td>
                                                        <div>{recall.title}</div>
                                                    </td>
                                                    <td>
                                                        <div className="small">{recall.createdAt}</div>
                                                    </td>
                                                    <td>
                                                        {getStatusBadge(recall.status)}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => handleEditClick(recall.id)}
                                                                title="View"
                                                            >
                                                                <i className="bi bi-eye"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => toggleRow(recall.id)}
                                                                title="Toggle Details"
                                                                aria-expanded={expandedRows.includes(recall.id)}
                                                            >
                                                                {expandedRows.includes(recall.id) ? (
                                                                    <i className="bi bi-chevron-up"></i>
                                                                ) : (
                                                                    <i className="bi bi-chevron-down"></i>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="5" className="p-0 border-top-0">
                                                        <Collapse in={expandedRows.includes(recall.id)}>
                                                            <div className="p-3">
                                                                <div className="row">
                                                                    <div className="col-md-6">
                                                                        <h5>Details</h5>
                                                                        <hr/>
                                                                        <div className="mb-2">
                                                                            ID: <span className="text-secondary small">{recall.id}</span>
                                                                        </div>
                                                                        <div className="mb-2">
                                                                            Notes: <span className="text-secondary small"> {recall.notes}</span>
                                                                        </div>
                                                                        <div className="mb-2">
                                                                            File: <span className="text-secondary small">{recall.filePath}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <h5>Admin Information</h5>
                                                                        <hr/>
                                                                        <div className="mb-2">
                                                                            Admin Notes: <span className="text-secondary small">{recall.adminNotes || 'N/A'}</span>
                                                                        </div>
                                                                        {recall.processedFilePath && (
                                                                            <div className="mb-2">
                                                                                Processed File: <span className="text-secondary small">{recall.processedFilePath}</span>
                                                                            </div>
                                                                        )}
                                                                        <div className="mb-2">
                                                                            Modified: <span className="text-secondary small">{recall.modifiedAt}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">
                                                <div className="alert alert-danger">
                                                    <i className="bi bi-exclamation-triangle"></i> {error ? error : "No recalls found. Try adjusting your filters."}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            {recallList.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="d-flex align-items-center">
                                        <span className="me-2">Show:</span>
                                        <select
                                            className="form-select form-select-sm w-auto"
                                            value={pagination.size}
                                            onChange={handleSizeChange}
                                        >
                                            {[10, 25, 50, 100].map(size => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                        <span className="ms-2 text-muted">
                                            Showing {startItem} to {endItem} of {pagination.totalElements} entries
                                        </span>
                                    </div>

                                    <Pagination>
                                        <Pagination.First
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(1)}
                                        />
                                        <Pagination.Prev
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(pagination.page)}
                                        />

                                        {Array.from({length: Math.min(5, pagination.totalPages)}, (_, i) => {
                                            const pageNum = i + 1;
                                            return (
                                                <Pagination.Item
                                                    key={pageNum}
                                                    active={pageNum === pagination.page + 1}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}

                                        <Pagination.Next
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.page + 2)}
                                        />
                                        <Pagination.Last
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.totalPages)}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RecallTable;